import { FirstPageRounded, KeyboardArrowLeftRounded, KeyboardArrowRightRounded, LastPageRounded } from '@mui/icons-material';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { action, observable, runInAction, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import TextSnippet from '../../../img/icones/TextSnippet';
import { ITipoMinuta, Minuta, Processo } from '../../../modelo';
import MinutaService from '../../../services/MinutaService';
import { b64DecodeUnicode } from '../../../utils/Base64Utils';
import EscaninhoStore from '../../../store/EscaninhoStore';
import PaginacaoStore from '../../../store/PaginacaoStore';
import ParametroStore from '../../../store/ParametroStore';
import VisualizacaoMinutaStore from '../../../store/VisualizacaoMinutaStore';

const SELETOR_TRIGGER_MINUTA = '[id^="visualizar-minuta-"]';

/** Lista, em ordem de exibição, os gatilhos de pré-visualização de minuta renderizados na página atual. */
const triggersRenderizados = (): HTMLElement[] =>
    Array.from(document.querySelectorAll<HTMLElement>(SELETOR_TRIGGER_MINUTA));

/**
 * Clica no gatilho de minuta selecionado assim que ele estiver no DOM. Usado após
 * trocar de página do escaninho, quando a linha alvo ainda não terminou de renderizar.
 */
const clicarTriggerQuandoDisponivel = (
    selecionar: (triggers: HTMLElement[]) => HTMLElement | undefined,
    tentativasRestantes = 30
) => {
    const alvo = selecionar(triggersRenderizados());
    if (alvo) {
        alvo.click();
        return;
    }
    if (tentativasRestantes > 0) {
        requestAnimationFrame(() => clicarTriggerQuandoDisponivel(selecionar, tentativasRestantes - 1));
    }
};

interface IPropsVisualizarMinuta {
    processo: Processo;
    minuta: Minuta;
}

class VisualizarMinuta extends React.Component<IPropsVisualizarMinuta> {
    minutaHTML: string = '';
    modalAberto: boolean = false;
    isLoading: boolean = false;

    constructor(props: IPropsVisualizarMinuta) {
        super(props);
        makeObservable(this, {
            minutaHTML: observable,
            modalAberto: observable,
            isLoading: observable,
            abrirMinuta: action,
            closeModal: action,
            mostrarPrimeiro: action,
            mostrarAnterior: action,
            mostrarProximo: action,
            mostrarUltimo: action
        });
    }

    componentWillUnmount() {
        if (VisualizacaoMinutaStore.identificadorProcesso === this.props.processo.identificadorProcesso) {
            VisualizacaoMinutaStore.limpar();
        }
    }

    abrirMinuta = async () => {
        runInAction(() => { 
            this.isLoading = true;
        });
        const numeroMinuta = this.props.minuta?.numero;
        if (!numeroMinuta) {
            runInAction(() => {
                this.isLoading = false;
            });
            return;
        }

        try {
            const minuta = await MinutaService.visualizarMinutaAssinatura(numeroMinuta);

            runInAction(() => {
                const conteudo = (minuta as any).conteudoPreparadoHtml || (minuta as any).conteudoOriginal;

                if (!conteudo || String(conteudo).trim() === '') {
                    this.minutaHTML = '<div style="padding:16px;color:#444">Não foi possível recuperar o conteúdo da minuta.</div>';
                    this.modalAberto = true;
                    return;
                }

                try {
                    const decoded = b64DecodeUnicode(conteudo);
                    const htmlAjustado = (decoded as string).replace('style="position: absolute;left: 0;top: -0.14in;padding-top: 12mm;padding-left: 26mm;width: 0.7362205in;height: 0.7637795in"', 'style="position: absolute;left: 0;top: -0.14in;margin-top: 12mm;margin-left: 26mm;width: 0.7362205in;height: 0.7637795in"');
                    const solicitouAposPauta = !!this.props.minuta?.autorizacaoPauta || !!(minuta as any)?.autorizacaoPauta;
                    this.minutaHTML = this.aplicarAposPautaNoPreviewFinal(htmlAjustado, solicitouAposPauta);
                    this.modalAberto = true;
                } catch (e) {
                    console.error('VisualizarMinuta: erro ao decodificar base64 da minuta', e);
                    this.minutaHTML = '<div style="padding:16px;color:#444">Não foi possível recuperar o conteúdo da minuta (formato inválido).</div>';
                    this.modalAberto = true;
                }
            });
        } catch (e) {
            console.error('VisualizarMinuta: erro ao abrir minuta', e);
            runInAction(() => {
                this.minutaHTML = '<div style="padding:16px;color:#444">Não foi possível visualizar a minuta no momento.</div>';
                this.modalAberto = true;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
            if (this.modalAberto) {
                VisualizacaoMinutaStore.definirProcesso(this.props.processo.identificadorProcesso);
                this.scrollParaLinhaProcesso();
            }
        }
    }

    scrollParaLinhaProcesso = () => {
        const elemento = document.getElementById('accordion-processo-' + this.props.processo.idProcessoEscaninho);
        elemento?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    normalizarTexto(texto: string): string {
        return (texto || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[.,;:!?()"']/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    aplicarAposPautaNoPreviewFinal(conteudoHtml: string, solicitouAposPauta: boolean): string {
        const tipo = this.props.minuta?.tipo?.codigo;
        const ehDespachoOuDecisao = tipo === 1 || tipo === 2;

        if (!solicitouAposPauta || !ehDespachoOuDecisao || !conteudoHtml) {
            return conteudoHtml;
        }

        const html = document.createElement('div');
        html.innerHTML = conteudoHtml;

        let elementosTexto = Array.from(html.querySelectorAll('p, div, span')) as HTMLElement[];
        const tagsDeBloco = ['P', 'DIV', 'LI'];

        const elementosAposPauta = elementosTexto.filter((el) => {
            const txt = this.normalizarTexto(el.textContent || '');
            return txt === 'apos a pauta' || /\bapos\s*,?\s*a\s+pauta\b|\bapos\s*,?\s+pauta\b/.test(txt);
        });

        elementosAposPauta.forEach((el) => {
            el.parentNode?.removeChild(el);
        });

        elementosTexto = Array.from(html.querySelectorAll('p, div, span')) as HTMLElement[];

        const candidatosPubliquese = elementosTexto
            .map((el) => ({
                el,
                texto: this.normalizarTexto(el.textContent || '')
            }))
            .filter((item) => (
                item.texto.length > 0
                && item.texto.length <= 80
                && (item.texto === 'publique-se' || item.texto === 'publiquese' || /\bpublique\s*-?\s*se\b/.test(item.texto))
            ))
            .sort((a, b) => a.texto.length - b.texto.length);

        const alvoPubliquese = candidatosPubliquese[0]?.el;

        const ancoraAssinatura = elementosTexto.find((el) => {
            if (el.getAttribute('data-sv-topico') === 'assinatura') {
                return true;
            }
            const txt = this.normalizarTexto(el.textContent || '');
            return /\b(ministro|ministra|relator|relatora|presidente|desembargador|desembargadora)\b/.test(txt);
        });

        const aplicarTextoMantendoEstrutura = (elemento: HTMLElement, novoTexto: string) => {
            const regexPubliquese = /publique\s*-?\s*se\.?/i;
            const walker = document.createTreeWalker(elemento, NodeFilter.SHOW_TEXT);

            let noAtual = walker.nextNode() as Text | null;
            while (noAtual) {
                const atual = noAtual.nodeValue || '';
                if (regexPubliquese.test(atual)) {
                    noAtual.nodeValue = atual.replace(regexPubliquese, novoTexto);
                    return;
                }
                noAtual = walker.nextNode() as Text | null;
            }

            elemento.textContent = novoTexto;
        };

        const obterBlocoDaLinha = (elemento: HTMLElement): HTMLElement => {
            let atual: HTMLElement | null = elemento;
            while (atual && atual !== html) {
                if (tagsDeBloco.includes(atual.tagName)) {
                    return atual;
                }
                atual = atual.parentElement;
            }
            return elemento;
        };

        let novoElemento: HTMLElement;
        let ancoraInsercao: HTMLElement | undefined;
        if (alvoPubliquese) {
            ancoraInsercao = obterBlocoDaLinha(alvoPubliquese);
            novoElemento = ancoraInsercao.cloneNode(true) as HTMLElement;
            aplicarTextoMantendoEstrutura(novoElemento, 'Após, à Pauta.');
        } else {
            novoElemento = document.createElement('p');
            novoElemento.textContent = 'Após, à Pauta.';
        }

        if (ancoraInsercao?.parentNode) {
            ancoraInsercao.parentNode.insertBefore(novoElemento, ancoraInsercao.nextSibling);
        } else if (ancoraAssinatura?.parentNode) {
            ancoraAssinatura.parentNode.insertBefore(novoElemento, ancoraAssinatura);
        } else {
            html.appendChild(novoElemento);
        }

        return html.innerHTML;
    }

    closeModal = () => {
        this.modalAberto = false;
        VisualizacaoMinutaStore.limpar();
    };

    get idTriggerAtual() {
        return 'visualizar-minuta-' + this.props.processo.identificadorProcesso + '-' + this.props.minuta?.tipo?.codigo;
    }

    /** Índice do gatilho da minuta atual entre os gatilhos renderizados na página. */
    indiceNaPagina = (triggers: HTMLElement[] = triggersRenderizados()): number =>
        triggers.findIndex(trigger => trigger.id === this.idTriggerAtual);

    get minutasNavegaveisDoEscaninho() {
        return EscaninhoStore.processosPorTipoEscaninhoFiltrado.flatMap(processo =>
            this.minutasNavegaveisDoProcesso(processo).map(minuta => ({ processo, minuta }))
        );
    }

    minutasNavegaveisDoProcesso = (processo: Processo) =>
        (processo.minutasVisiveis || processo.minutas)
            .filter(minuta => minuta.mostrar && minuta.numero && minuta.tipo?.codigo);

    indiceNoEscaninho = () =>
        this.minutasNavegaveisDoEscaninho.findIndex(({ minuta }) => minuta.numero === this.props.minuta.numero);

    abrirMinutaNavegavel = (processo: Processo, minuta: Minuta) => {
        const triggers = triggersRenderizados();
        const trigger = triggers.find(item =>
            item.id === 'visualizar-minuta-' + processo.identificadorProcesso + '-' + minuta.tipo.codigo
        );
        if (trigger) {
            this.closeModal();
            trigger.click();
            return true;
        }
        if (ParametroStore.visualizacaoCompacta && ParametroStore.anchorEl) {
            this.closeModal();
            ParametroStore.openPopoverMinutaCompacta(ParametroStore.anchorEl, minuta, processo);
            return true;
        }
        return false;
    };

    get temAnterior() {
        return this.indiceNaPagina() > 0 || this.indiceNoEscaninho() > 0 || PaginacaoStore.pagina > 1;
    }

    get temProximo() {
        const triggers = triggersRenderizados();
        const indice = this.indiceNaPagina(triggers);
        return (indice >= 0 && indice < triggers.length - 1)
            || (this.indiceNoEscaninho() >= 0 && this.indiceNoEscaninho() < this.minutasNavegaveisDoEscaninho.length - 1)
            || PaginacaoStore.pagina < PaginacaoStore.qtdPaginas;
    }

    /**
     * Vai para `novaPagina` (se necessário) e clica no gatilho escolhido assim que a
     * página estiver renderizada. Quando a página muda, os gatilhos que já estavam no
     * DOM são ignorados para não clicar por engano numa linha da página anterior.
     */
    navegarParaPagina = (novaPagina: number, escolher: (triggers: HTMLElement[]) => HTMLElement | undefined) => {
        const mudouPagina = novaPagina !== PaginacaoStore.pagina;
        const idsAntes = new Set(triggersRenderizados().map(trigger => trigger.id));
        this.closeModal();
        if (mudouPagina) {
            PaginacaoStore.handleTrocaPagina(novaPagina);
        }
        clicarTriggerQuandoDisponivel(triggers => escolher(
            mudouPagina ? triggers.filter(trigger => !idsAntes.has(trigger.id)) : triggers
        ));
    };

    mostrarPrimeiro = () => {
        this.navegarParaPagina(1, triggers => triggers[0]);
    };

    mostrarUltimo = () => {
        this.navegarParaPagina(PaginacaoStore.qtdPaginas, triggers => triggers[triggers.length - 1]);
    };

    mostrarProximo = () => {
        const triggers = triggersRenderizados();
        const indice = this.indiceNaPagina(triggers);
        if (indice >= 0 && indice < triggers.length - 1) {
            this.closeModal();
            triggers[indice + 1].click();
            return;
        }
        const indiceEscaninho = this.indiceNoEscaninho();
        if (indiceEscaninho >= 0 && indiceEscaninho < this.minutasNavegaveisDoEscaninho.length - 1) {
            const proxima = this.minutasNavegaveisDoEscaninho[indiceEscaninho + 1];
            if (this.abrirMinutaNavegavel(proxima.processo, proxima.minuta)) {
                return;
            }
        }
        if (PaginacaoStore.pagina < PaginacaoStore.qtdPaginas) {
            this.navegarParaPagina(PaginacaoStore.pagina + 1, novos => novos[0]);
        }
    };

    mostrarAnterior = () => {
        const triggers = triggersRenderizados();
        const indice = this.indiceNaPagina(triggers);
        if (indice > 0) {
            this.closeModal();
            triggers[indice - 1].click();
            return;
        }
        const indiceEscaninho = this.indiceNoEscaninho();
        if (indiceEscaninho > 0) {
            const anterior = this.minutasNavegaveisDoEscaninho[indiceEscaninho - 1];
            if (this.abrirMinutaNavegavel(anterior.processo, anterior.minuta)) {
                return;
            }
        }
        if (PaginacaoStore.pagina > 1) {
            this.navegarParaPagina(PaginacaoStore.pagina - 1, novos => novos[novos.length - 1]);
        }
    };

    atalhos = (ev) => {
        switch (ev.key) {
            case 'ArrowDown':
            case 'ArrowRight':
            case 'PageDown':
                this.mostrarProximo();
                break;
            case 'ArrowUp':
            case 'ArrowLeft':
            case 'PageUp':
                this.mostrarAnterior();
                break;
            case 'Home':
                this.mostrarPrimeiro();
                break;
            case 'End':
                this.mostrarUltimo();
                break;
            default:
                return;
        }
        ev.preventDefault();
    }


    render() {
        const codigoTipoMinuta = this.props.minuta?.tipo?.codigo;
        if (!codigoTipoMinuta) {
            return null;
        }
        const tooltipTitle = `Visualizar o conteúdo da minuta de ${this.props.minuta.tipo?.descricao?.toLowerCase()}`;
        const temAnterior = this.temAnterior;
        const temProximo = this.temProximo;
        return (
            <>
                <Tooltip title={tooltipTitle}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        {!this.isLoading ? <IconButton size='small' onClick={this.abrirMinuta} id={'visualizar-minuta-' + this.props.processo.identificadorProcesso + '-' + codigoTipoMinuta}
                            aria-label={tooltipTitle}>
                            <TextSnippet />
                        </IconButton> :  <CircularProgress aria-label='Carregando minuta..' size={22} style={{margin: 6}}/>}
                    </div>
                </Tooltip>
                {this.modalAberto &&
                    <Dialog open={this.modalAberto} maxWidth='md' onClose={this.closeModal} onKeyUp={this.atalhos}>
                        <DialogTitle>
                            Visualizando {this.props.minuta.tipo?.descricao} do processo {this.props.processo.numeroProcesso}
                        </DialogTitle>
                        <DialogContent>
                            <div dangerouslySetInnerHTML={{ __html: this.minutaHTML }}>
                            </div>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button size='small' onClick={this.mostrarPrimeiro} disabled={!temAnterior}
                                    startIcon={<FirstPageRounded />}>
                                    Primeiro
                                </Button>
                                <Button size='small' onClick={this.mostrarAnterior} disabled={!temAnterior}
                                    startIcon={<KeyboardArrowLeftRounded />}>
                                    Anterior
                                </Button>
                                <Button size='small' onClick={this.mostrarProximo} disabled={!temProximo}
                                    endIcon={<KeyboardArrowRightRounded />}>
                                    Próximo
                                </Button>
                                <Button size='small' onClick={this.mostrarUltimo} disabled={!temProximo}
                                    endIcon={<LastPageRounded />}>
                                    Último
                                </Button>
                            </Box>
                            <Button variant='contained' color='primary' onClick={this.closeModal}>
                                Fechar
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
            </>
        );
    }
}

export default observer(VisualizarMinuta);
